import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  TrendingUp,
  FileText,
  Users,
  Truck,
  LayoutGrid,
  Package,
  DollarSign,
  CookingPot,
  Warehouse,
  LogOut,
  X,
  UserCheck,
  Database,
  Wallet,
  HardHat,
  Settings,
  ClipboardList,
  Banknote,
  Receipt,
} from "lucide-react";
import UserAvatar from "./ProfileSettings.js";

const ADMIN_PRIMARY_TABS = [
  { id: "dashboard", path: "/dashboard", icon: TrendingUp, label: "Inicio" },
  { id: "invoices", path: "/invoices", icon: FileText, label: "Facturas" },
  { id: "clients", path: "/clients", icon: Users, label: "Clientes" },
  { id: "providers", path: "/providers", icon: Truck, label: "Leche" },
];

const SELLER_PRIMARY_TABS = [
  { id: "invoices", path: "/invoices", icon: FileText, label: "Facturas" },
  { id: "payments", path: "/payments", icon: DollarSign, label: "Pagos" },
  { id: "clients", path: "/clients", icon: Users, label: "Clientes" },
];

const ADMIN_MORE_GROUPS = [
  {
    title: "Producción",
    items: [
      { id: "providers", path: "/providers", icon: Truck, label: "Leche" },
      { id: "cheeses", path: "/cheeses", icon: CookingPot, label: "Quesos" },
      { id: "maturation", path: "/maturation", icon: Warehouse, label: "Maduración" },
    ],
  },
  {
    title: "Movimientos",
    items: [
      { id: "provider-payments", path: "/provider-payments", icon: Banknote, label: "Pagos a proveedores" },
      { id: "payments", path: "/payments", icon: DollarSign, label: "Reportes de pago" },
    ],
  },
  {
    title: "Gastos",
    items: [
      { id: "expenses", path: "/expenses", icon: Receipt, label: "Gastos de quesera" },
    ],
  },
  {
    title: "Nómina",
    items: [
      { id: "employees", path: "/employees", icon: HardHat, label: "Empleados" },
      { id: "payroll", path: "/payroll", icon: Wallet, label: "Nómina" },
    ],
  },
  {
    title: "Sistema",
    items: [
      { id: "sellers", path: "/sellers", icon: UserCheck, label: "Vendedores" },
      { id: "queseros", path: "/queseros", icon: CookingPot, label: "Queseros" },
      { id: "products", path: "/products", icon: Package, label: "Productos" },
      { id: "invoice-activity", path: "/invoice-activity", icon: ClipboardList, label: "Registro" },
      { id: "backup", path: "/backup", icon: Database, label: "Backup" },
    ],
  },
];

const SELLER_MORE_ITEMS = [];

function isActive(path, currentPath) {
  if (path === "/dashboard") {
    return currentPath === "/" || currentPath === "/dashboard";
  }
  return currentPath === path || currentPath.startsWith(`${path}/`);
}

const MobileBottomNav = ({ onLogout, userName, user, role = "admin", permissions = {}, onOpenProfile }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [moreOpen, setMoreOpen] = useState(false);
  const isAdmin = !role || role === "admin";
  const isQuesero = role === "quesero";
  const queseroTabs = [
    permissions.manageMilk && { id: "providers", path: "/providers", icon: Truck, label: "Leche" },
    permissions.manageCheese && { id: "cheeses", path: "/cheeses", icon: CookingPot, label: "Quesos" },
  ].filter(Boolean);

  const primaryTabs = isAdmin ? ADMIN_PRIMARY_TABS : isQuesero ? queseroTabs : SELLER_PRIMARY_TABS;
  const moreGroups = isAdmin ? ADMIN_MORE_GROUPS : [];
  const moreItems = isAdmin
    ? ADMIN_MORE_GROUPS.flatMap((group) => group.items)
    : SELLER_MORE_ITEMS;
  const moreActive = moreItems.some((item) => isActive(item.path, currentPath));

  const closeMore = () => setMoreOpen(false);

  return (
    <>
      {moreOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/40 md:hidden"
          onClick={closeMore}
          aria-hidden
        />
      )}

      {moreOpen && (
        <div
          className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] z-50 md:hidden px-3 pb-2"
          role="dialog"
          aria-label="Más opciones"
        >
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden max-h-[min(70vh,520px)] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3 min-w-0">
                {user ? <UserAvatar user={user} size="sm" className="rounded-lg" /> : null}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900">Más opciones</p>
                  {userName && (
                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{userName}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {onOpenProfile && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMore();
                      onOpenProfile();
                    }}
                    className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-gray-50"
                    aria-label="Configuración del perfil"
                    title="Configuración del perfil"
                  >
                    <Settings size={18} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeMore}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <nav className="p-2 overflow-y-auto">
              {moreGroups.map((group) => (
                <div key={group.title} className="mb-2">
                  <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {group.title}
                  </p>
                  {group.items.map(({ id, path, icon: Icon, label }) => {
                    const active = isActive(path, currentPath);
                    return (
                      <Link
                        key={id}
                        to={path}
                        onClick={closeMore}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          active
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <Icon size={20} className={active ? "text-indigo-600" : "text-gray-400"} />
                        {label}
                      </Link>
                    );
                  })}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  closeMore();
                  onLogout?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 mt-1"
              >
                <LogOut size={20} />
                Cerrar sesión
              </button>
            </nav>
          </div>
        </div>
      )}

      <nav
        className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(15,23,42,0.08)] pb-[env(safe-area-inset-bottom,0px)]"
        aria-label="Navegación principal"
      >
        <div className="flex items-stretch justify-around h-[3.75rem] max-w-lg mx-auto">
          {primaryTabs.map(({ id, path, icon: Icon, label }) => {
            const active = isActive(path, currentPath);
            return (
              <Link
                key={id}
                to={path}
                onClick={closeMore}
                className={`flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 px-1 transition-colors ${
                  active ? "text-indigo-600" : "text-gray-500"
                }`}
              >
                <Icon size={22} strokeWidth={active ? 2.5 : 2} />
                <span className={`text-[10px] font-medium truncate w-full text-center ${active ? "font-semibold" : ""}`}>
                  {label}
                </span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            className={`flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 px-1 transition-colors ${
              moreOpen || moreActive ? "text-indigo-600" : "text-gray-500"
            }`}
            aria-expanded={moreOpen}
            aria-label="Más opciones"
          >
            <LayoutGrid size={22} strokeWidth={moreOpen || moreActive ? 2.5 : 2} />
            <span className={`text-[10px] font-medium ${moreOpen || moreActive ? "font-semibold" : ""}`}>
              Más
            </span>
          </button>
        </div>
      </nav>
    </>
  );
};

export const MOBILE_PAGE_TITLES = {
  "/": "Dashboard",
  "/dashboard": "Dashboard",
  "/clients": "Clientes",
  "/sellers": "Vendedores",
  "/queseros": "Queseros",
  "/products": "Productos",
  "/providers": "Proveedores de leche",
  "/cheeses": "Quesos",
  "/maturation": "Maduración",
  "/provider-payments": "Movimientos de pago",
  "/expenses": "Gastos",
  "/employees": "Empleados",
  "/payroll": "Nómina",
  "/invoices": "Facturación",
  "/payments": "Reportes de pago",
  "/invoice-activity": "Registro de facturas",
  "/backup": "Backup",
};

export default MobileBottomNav;
